import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { RootState } from '../Components/Redux/store';
import { setAuth } from '../Components/Redux/authSlice';
import 'bootstrap/dist/css/bootstrap.min.css';

declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}

export default function AdmissionReport() {
  const [classes, setClasses] = useState<any[]>([]);
  const [selectedClass, setSelectedClass] = useState<number | null>(null);
  const [students, setStudents] = useState<any[]>([]);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth.value);

  const getCookie = (name: string) => {
    const cookieName = name + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i];
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1);
      }
      if (cookie.indexOf(cookieName) === 0) {
        return cookie.substring(cookieName.length, cookie.length);
      }
    }
    return '';
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = getCookie('token');
        if (!token) {
          dispatch(setAuth(false));
          return;
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        await axios.get('/users/user');
        dispatch(setAuth(true));
      } catch (e) {
        dispatch(setAuth(false));
      }
    };
    fetchUserData();
  }, [dispatch]);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await axios.get('/Branches/classes/');
        setClasses(response.data);
      } catch (error) {
        console.error('Error fetching classes:', error);
      }
    };

    fetchClasses();
  }, []);

  useEffect(() => {
    if (selectedClass) {
      const fetchStudents = async () => {
        setIsLoading(true);
        try {
          const params: any = { class_id: selectedClass };
          if (startDate) params.start_date = startDate;
          if (endDate) params.end_date = endDate;

          const response = await axios.get('/Branches/students/', { params });
          setStudents(response.data);
        } catch (error) {
          console.error('Error fetching students:', error);
          setStudents([]);
        } finally {
          setIsLoading(false);
        }
      };

      fetchStudents();
    }
  }, [selectedClass, startDate, endDate]);

  const handleClassChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const classId = Number(e.target.value);
    setSelectedClass(classId);
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    doc.text('Warbixinta Diiwaangellinta', 14, 16);
    doc.autoTable({
      head: [['Magaca', 'Lambarka Waalidka', 'Cinwaanka', 'Degmada', 'Taariikhda Diiwaangellinta']],
      body: students.map((student) => [
        student.name,
        student.guardian_number,
        student.address,
        student.district,
        student.date_reg,
      ]),
      startY: 20,
    });
    doc.save('Warbixinta_Diiwaangellinta.pdf');
  };

  const printReport = () => {
    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow?.document.write('<html><head><title>Daabac Warbixinta</title>');
    printWindow?.document.write('<style>@media print { .no-print { display: none; } }</style>');
    printWindow?.document.write('</head><body>');
    printWindow?.document.write(document.querySelector('.printable-content')?.innerHTML || '');
    printWindow?.document.write('</body></html>');
    printWindow?.document.close();
    printWindow?.focus();
    printWindow?.print();
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(students.map(student => ({
      Magaca: student.name,
      Lambarka_Waalidka: student.guardian_number,
      Cinwaanka: student.address,
      Degmada: student.district,
      Taariikhda_Diiwaangellinta: student.date_reg,
    })));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Ardayda');
    XLSX.writeFile(workbook, 'Warbixinta_Diiwaangellinta.xlsx');
  };

  return (
    <div className="container-fluid py-4" style={{ marginLeft: '220px', maxWidth: 'calc(100vw - 240px)' }}>
      {auth ? (
        <>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="mb-0">Warbixinta Diiwaangellinta Ardayda</h2>
            <div className="d-flex">
              <button className="btn btn-outline-primary me-2" onClick={generatePDF}>
                <i className="bi bi-file-earmark-pdf me-1"></i> PDF
              </button>
              <button className="btn btn-outline-secondary me-2" onClick={printReport}>
                <i className="bi bi-printer me-1"></i> Daabac
              </button>
              <button className="btn btn-outline-success" onClick={exportToExcel}>
                <i className="bi bi-file-earmark-excel me-1"></i> Excel
              </button>
            </div>
          </div>

          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <h5 className="card-title mb-4">Shaandhee Ardayda</h5>
              <div className="row g-3">
                <div className="col-md-4">
                  <label htmlFor="classSelect" className="form-label fw-semibold">Fasalka</label>
                  <select
                    id="classSelect"
                    onChange={handleClassChange}
                    value={selectedClass || ''}
                    className="form-select"
                  >
                    <option value="" disabled>Dooro fasal</option>
                    {classes.map((cls) => (
                      <option key={cls.id} value={cls.id}>
                        {cls.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label htmlFor="startDate" className="form-label fw-semibold">Taariikhda Bilowga</label>
                  <input
                    type="date"
                    id="startDate"
                    value={startDate}
                    onChange={handleStartDateChange}
                    className="form-control"
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="endDate" className="form-label fw-semibold">Taariikhda Dhamaadka</label>
                  <input
                    type="date"
                    id="endDate"
                    value={endDate}
                    onChange={handleEndDateChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="d-flex justify-content-center my-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Soo dejinaya...</span>
              </div>
            </div>
          ) : students.length > 0 ? (
            <div className="card shadow-sm">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th scope="col">Magaca</th>
                        <th scope="col">Lambarka Waalidka</th>
                        <th scope="col">Cinwaanka</th>
                        <th scope="col">Degmada</th>
                        <th scope="col">Taariikhda Diiwaangellinta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((student, index) => (
                        <tr key={index}>
                          <td className="fw-semibold">{student.name}</td>
                          <td>{student.guardian_number}</td>
                          <td>{student.address}</td>
                          <td>
                            <span className="badge bg-secondary bg-opacity-10 text-dark">
                              {student.district || 'Lama Helin'}
                            </span>
                          </td>
                          <td>{new Date(student.date_reg).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="card shadow-sm">
              <div className="card-body text-center py-5">
                <i className="bi bi-people display-4 text-muted"></i>
                <p className="mt-3 mb-0 text-muted">
                  {selectedClass
                    ? "Lama helin arday u dhiganta fasalka iyo taariikhda la doortay."
                    : "Fadlan dooro fasal si aad u aragto diiwaanka ardayda."}
                </p>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="card shadow-sm">
          <div className="card-body text-center py-5">
            <i className="bi bi-shield-lock display-4 text-muted"></i>
            <p className="mt-3 mb-0 text-muted">
              Fadlan <Link to="/login">soo gal</Link> si aad u aragto warbixinta diiwaangellinta.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}